import {
  MentorshipDetails,
  useMentorNotificationsCount,
} from "@/lib/api/mentorships.ts";
import { MentorshipName } from "@/components/mentorships/mentorship-name.tsx";
import { ChevronLeftIcon } from "@radix-ui/react-icons";
import { useRouter } from "@tanstack/react-router";
import { Inbox } from "@/components/inbox/inbox";
import { useState } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover.tsx";
import { Button } from "@/components/ui/button.tsx";
import {
  ViewMemoriesButton,
  NotificationStatusIcon,
} from "@/components/conversations/channel-header.tsx";
import { ChurnAnalysisButton } from "@/components/conversations/churn-analysis-button";
import { useHubStore } from "@/components/hub/use-hub-store";
import { cn } from "@/lib/utils";

interface HeaderProps {
  mentorship: MentorshipDetails;
}

export function Header({ mentorship }: HeaderProps) {
  const { history } = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const notificationsCount = useMentorNotificationsCount(mentorship.mentor.id);
  const isMenteeResearchParticipant =
    mentorship.mentee.profile?.metadata?.researchConsent?.consent === true;
  const isHubOpen = useHubStore((state) => state.isOpen);
  const toggleHub = useHubStore((state) => state.toggle);

  return (
    <div className="flex flex-row items-center gap-2 border-b px-4 py-3">
      <button
        onClick={() => {
          history.back();
        }}
      >
        <ChevronLeftIcon className="h-6 w-6" />
      </button>
      <MentorshipName
        mentorName={mentorship.mentor.name ?? ""}
        menteeName={mentorship.mentee.name}
        menteeUserId={mentorship.mentee.id}
        menteeRedactedFields={mentorship.mentee.redacted_fields}
        mentorProfilePictureUrl={mentorship.mentor.profile_picture_url!}
        menteeProfilePictureUrl={mentorship.mentee.profile_picture_url!}
        isMenteeResearchParticipant={isMenteeResearchParticipant}
        notificationsCount={notificationsCount}
        onNotificationsClick={() => {
          setIsOpen(true);
        }}
      />

      <NotificationStatusIcon userId={mentorship.mentee.id} className="ml-4" />

      <div className="flex-1"></div>

      {/* Right side tools popover */}
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" className="ml-2">
            View tools
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-64 rounded-2xl p-0 shadow-lg">
          <div className="flex flex-col py-2">
            <ViewMemoriesButton menteeId={mentorship.mentee.id} />
            <ChurnAnalysisButton
              mentorshipId={mentorship?.id || ""}
              isDeactivated={mentorship?.deactivated || false}
            />
          </div>
        </PopoverContent>
      </Popover>

      <Button
        variant="outline"
        className={cn("ml-2", isHubOpen && "bg-[#E2E2E2]")}
        onClick={toggleHub}
      >
        Hub
      </Button>

      <Inbox
        userId={mentorship.mentor.id}
        isOpen={isOpen}
        teenId={mentorship.mentee.id}
        close={() => setIsOpen(false)}
      />
    </div>
  );
}
